import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  ActivityIndicator,
  SectionList,
  SectionListData,
  SectionListRenderItemInfo,
  StyleSheet,
  View,
  ViewProps,
} from 'react-native'
import {
  fetchAllPages,
  LoggingMessage,
  useCompany,
  useFetchProductLogging,
  useInfiniteProductLogging,
  useProduct,
  useStore,
} from 'zens-store'
import {
  shortLocalizedDate,
  shortLocalizedTime,
  Text,
  Color,
  Spacing,
  PopUp,
  Hyperlink,
  localizedTime,
  Button,
  Badge,
  Font,
} from 'zens-storybook'
import * as XLSX from 'xlsx'

import { useArrayParams } from 'zens-router'

import { InfoTable, InfoTableConfig } from '../info'
import { Panel } from '../layout'
import { MenuMore } from '../menu'

import { TimeZoneConflict } from '../location'

import ActivityFilter from './filter'

const styles = StyleSheet.create({
  container: {},
  content: {
    flex: 1,
    flexDirection: 'row',
  },
  row: {
    flexDirection: 'row',
  },
  time: {
    width: 45,
    textAlign: 'right',
    color: Color.metalGrey,
  },
  timeline: {
    alignItems: 'center',
  },
  line: {
    width: 2,
    height: 5,
    backgroundColor: Color.calmGrey,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Color.calmGrey,
    marginVertical: 4,
    marginHorizontal: Spacing.S,
  },
  dotSection: {
    backgroundColor: Color.premiumBlack,
  },
  invisible: {
    backgroundColor: Color.transparent,
  },
  title: {
    textDecorationLine: 'none',
  },
  popup: {
    minWidth: 800,
    padding: Spacing.L,
  },
  more: {
    alignSelf: 'flex-start',
    marginLeft: Spacing.L,
    marginTop: Spacing.S,
    marginVertical: Spacing.XXS,
  },
  loading: {
    alignSelf: 'flex-start',
    marginLeft: Spacing.XL,
    marginTop: Spacing.S,
  },
  empty: {
    color: Color.metalGrey,
  },
  activity: {
    marginRight: Spacing.XS,
  },
  skeletonList: {
    flex: 1,
  },
  moreMenu: {
    zIndex: 20,
    marginRight: -Spacing.S,
  },
  download: {
    height: 38,
  },
  csq: {
    flexDirection: 'row',
    width: 60,
    alignItems: 'center',
  },
  csqBar: {
    width: 38,
    height: 14,
    marginLeft: Spacing.XS,
    padding: 2,
    borderColor: Color.graphicGrey,
    borderWidth: 1,
  },
  csqValue: {
    height: '100%',
  },
  csqText: {
    width: 18,
    color: Color.metalGrey,
    textAlign: 'right',
  },
  csqTitle: {
    color: Color.metalGrey,
    fontFamily: Font.medium,
    marginLeft: 26,
    flex: 1,
  },
})

export type Activity =
  | 'sync'
  | 'firmware'
  | 'occupied'
  | 'charging'
  | 'occupied_charging'
  | 'available'
  | 'pdp_cycle'
  | 'power_cycle'
  | 'incomplete'
  | 'csq'
  | 'unknown'

interface Data {
  time?: Date
  activities: Activity[]
  message: LoggingMessage
}

interface Props extends ViewProps {
  companyId: number
  buildingId: number
  productId: number
}

const ActivityTimeline = (props: Props) => {
  const { companyId, buildingId, productId, style, ...otherProps } = props
  const [params, setParams] = useArrayParams<Activity>()
  const filter: Activity[] = params?.filter || [
    'firmware',
    'occupied',
    'charging',
    'occupied_charging',
    'available',
    'pdp_cycle',
    'power_cycle',
    'incomplete',
  ]
  const [messageInfo, setMessageInfo] = useState<false | InfoTableConfig>(false)
  const [downloading, setDownloading] = useState(false)
  const { i18n, message } = useStore()
  const { t } = useTranslation()
  const company = useCompany({ companyId })
  const product = useProduct({ companyId, productId })
  const messages = useInfiniteProductLogging({
    companyId,
    productId,
    sort: { field: 'time', direction: 'desc' },
    pagination: { page: 0, size: 1000 },
  })
  const fetchProductLogging = useFetchProductLogging()

  // @ts-expect-error Flatmap typing goes wrong here
  const allData = messages.data?.pages?.filter((page) => !!page)?.flatMap<LoggingMessage>((page) => page?.data) || []
  const data = dataForMessages(allData)
  const sections = data.reduce(
    (acc, item) => {
      if (!acc.length || !item.time || acc[acc.length - 1].title !== shortLocalizedDate(item.time, i18n.language, t)) {
        acc.push({
          title: item.time ? `${shortLocalizedDate(item.time, i18n.language, t)}` : '-',
          index: acc.length,
          data: [item],
        })
      } else {
        acc[acc.length - 1].data.push(item)
      }
      return acc
    },
    [] as { title: string; index: number; data: Data[] }[],
  )

  function updateFilter(value: Activity[]) {
    setParams({ filter: value })
  }

  async function download() {
    try {
      setDownloading(true)

      const data = await fetchAllPages<LoggingMessage>((page) =>
        fetchProductLogging({
          companyId,
          productId,
          pagination: { page, size: 1000 },
          sort: { field: 'time', direction: 'desc' },
        }),
      )

      const rows: (string | number | boolean | Date | null | undefined)[][] = []
      rows.push([t('Company'), company.data?.name])
      rows.push([t('Product serial number'), product.data?.serialNumber])
      rows.push([])
      rows.push([
        t('Received at'),
        t('Sent at'),
        t('Occupied'),
        t('Charging'),
        t('Led cnnct'),
        t('Prscn cnnct'),
        t('PDP'),
        t('Power cycle'),
        t('CSQ'),
        t('Firmware'),
      ])

      data?.forEach((message) => {
        rows.push([
          message.updatedAt,
          message.sentAt,
          message.isOccupied,
          message.isCharging,
          message.ledConnected,
          message.sensorConnected,
          message.pdpCycle,
          message.powerCycle,
          message.csq,
          message.firmware,
        ])
      })
      const worksheet = XLSX.utils.aoa_to_sheet(rows, { dateNF: 'm/d/yy h:mm' })
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')

      XLSX.writeFile(workbook, `${t('Messages')}.xlsx`, { compression: true })
      setDownloading(false)
    } catch (error) {
      message.add({ message: t('Error generating data'), status: 'error' })
      console.error('Error generating data', error)
      setDownloading(false)
    }
  }

  function showMessage(message: LoggingMessage) {
    const {
      updatedAt,
      sentAt,
      isOccupied,
      isCharging,
      ledConnected,
      sensorConnected,
      pdpCycle,
      powerCycle,
      csq,
      firmware,
    } = message
    setMessageInfo({
      fields: [
        {
          label: t('Received at'),
          value: updatedAt
            ? `${shortLocalizedDate(updatedAt, i18n.language)} ${localizedTime(updatedAt, i18n.language)}`
            : '-',
        },
        {
          label: t('Sent at'),
          value: sentAt ? `${shortLocalizedDate(sentAt, i18n.language)} ${localizedTime(sentAt, i18n.language)}` : '-',
        },
        {
          label: t('Occupied'),
          value: isOccupied === false ? t('No') : isOccupied ? t('Yes') : '-',
        },
        {
          label: t('Charging'),
          value: isCharging === false ? t('No') : isCharging ? t('Yes') : '-',
        },
        {
          label: t('Led cnnct'),
          value: ledConnected === false ? t('No') : ledConnected ? t('Yes') : '-',
        },
        {
          label: t('Prscn cnnct'),
          value: sensorConnected === false ? t('No') : sensorConnected ? t('Yes') : '-',
        },
        {
          label: t('PDP'),
          value: pdpCycle === false ? t('No') : pdpCycle ? t('Yes') : '-',
        },
        {
          label: t('Power cycle'),
          value: powerCycle === false ? t('No') : powerCycle ? t('Yes') : '-',
        },
        {
          label: t('CSQ'),
          value: csq ? csq : '-',
        },
        {
          label: t('Firmware'),
          value: firmware ? `v${firmware}` : '-',
        },
      ],
    })
  }

  function dataForMessages(messages: LoggingMessage[]): Data[] {
    return messages
      .map((message, index) => {
        const previousOccupancyMessage = messages
          .slice(index + 1)
          .find(
            (message) =>
              (message.isOccupied !== undefined && message.isOccupied !== null) ||
              (message.isCharging !== undefined && message.isCharging !== null),
          )
        const keys = (Object.keys(message.data) as (keyof LoggingMessage['data'])[]).filter(
          (key) =>
            message.data[key] !== undefined &&
            message.data[key] !== null &&
            ['id', 'time', 'rcnt', 'created_at', 'updated_at'].indexOf(key) === -1,
        )
        const activities: Activity[] = []
        if (keys.length === 0) {
          activities.push('sync')
        } else {
          if (
            (message.isOccupied !== undefined &&
              message.isOccupied !== null &&
              message.isOccupied !== previousOccupancyMessage?.isOccupied) ||
            (message.isCharging !== undefined &&
              message.isCharging !== null &&
              message.isCharging !== previousOccupancyMessage?.isCharging)
          ) {
            if (message.isOccupied && message.isCharging) activities.push('occupied_charging')
            else if (message.isOccupied) activities.push('occupied')
            else if (message.isCharging) activities.push('charging')
            else activities.push('available')
          }
          if (message.pdpCycle === true) activities.push('pdp_cycle')
          if (message.powerCycle === true) activities.push('power_cycle')

          if (message.ledConnected === false || message.sensorConnected === false) activities.push('incomplete')
          if (message.firmware) activities.push('firmware')
          if (activities.length === 0) activities.push('unknown')
        }
        const item = {
          time: message.sentAt,
          activities,
          message,
        }
        return item
      })
      .map((item) => ({ ...item, activities: item.activities.filter((activity) => filter.indexOf(activity) >= 0) }))
      .filter((item) => item.activities.length > 0)
  }

  function titleForActivity(item: Data) {
    const { activities, message: data } = item

    const activityBadges = activities.map((activity) => {
      switch (activity) {
        case 'sync':
          return <Badge style={styles.activity} title={t('Synchronization')} status="inactive" light />
        case 'firmware':
          return <Badge style={styles.activity} title={`${t('Firmware version')} v${data.firmware}`} />
        case 'occupied':
          return <Badge style={styles.activity} title={t('Workspace occupied')} status="error" light />
        case 'charging':
          return <Badge style={styles.activity} title={t('Workspace charging')} status="error" light />
        case 'occupied_charging':
          return <Badge style={styles.activity} title={t('Workspace occupied and charging')} status="error" light />
        case 'available':
          return <Badge style={styles.activity} title={t('Workspace free')} status="success" light />
        case 'pdp_cycle':
          return <Badge style={styles.activity} title={t('PDP cycle')} status="error" />
        case 'power_cycle':
          return <Badge style={styles.activity} title={t('Power cycle')} status="error" />
        case 'incomplete':
          return (
            <Badge
              style={styles.activity}
              title={`${t('{{part}} not connected', {
                part: [!data.ledConnected && t('Led'), !data.sensorConnected && t('Sensor')]
                  .filter((m) => !!m)
                  .join(` ${t('and')} `),
              })}`}
              status="error"
            />
          )
        default:
          return <Badge style={styles.activity} title={t('Repeated occupancy')} light />
      }
    })
    return activityBadges
  }

  function renderHeader(info: { section: SectionListData<Data> }) {
    const { section } = info
    const { title, index } = section
    return (
      <View style={styles.row}>
        {filter.indexOf('csq') !== -1 && (
          <View style={styles.csq}>
            {index === 0 && (
              <Text level={7} style={styles.csqTitle}>
                <Trans>CSQ</Trans>
              </Text>
            )}
          </View>
        )}
        <View style={styles.time} />
        <View style={styles.timeline}>
          <View style={[styles.line, index === 0 && styles.invisible]} />
          <View style={[styles.dot, styles.dotSection]} />
          <View style={styles.line} />
        </View>
        <Text level={5}>{title}</Text>
      </View>
    )
  }
  function renderItem(info: SectionListRenderItemInfo<Data>) {
    const { item, index, section } = info
    const { time, message } = item
    const csqStyle = {
      width: Math.min(message.csq || 0, 32),
      backgroundColor:
        message.csq && message.csq < 6
          ? Color.soothingPink
          : message.csq && message.csq < 8
            ? Color.collCopper
            : Color.refinedGreen,
    }

    return (
      <View style={styles.row}>
        {filter.indexOf('csq') !== -1 && (
          <View style={styles.csq}>
            {message.csq && (
              <>
                <Text style={styles.csqText} level={7}>
                  {message.csq}
                </Text>
                <View style={styles.csqBar}>
                  <View style={[styles.csqValue, csqStyle]} />
                </View>
              </>
            )}
          </View>
        )}
        <Text style={styles.time} level={7}>
          {time ? shortLocalizedTime(time) : '-'}
        </Text>
        <View style={styles.timeline}>
          <View style={styles.line} />
          <View style={styles.dot} />
          <View style={[styles.line, index === section.data?.length - 1 && styles.invisible]} />
        </View>
        <Hyperlink
          onPress={() => showMessage(message)}
          title={titleForActivity(item) as any}
          textStyle={styles.title}
          testID="ActivityTimeline.Row"
        />
      </View>
    )
  }
  return (
    <Panel
      style={[styles.container, style]}
      title={t('Activities')}
      right={
        downloading ? (
          <ActivityIndicator
            size={20}
            style={styles.download}
            color={Color.premiumBlack}
            testID="ActivityTimeline.Spinner"
          />
        ) : (
          <MenuMore
            items={[
              {
                key: 'data',
                icon: 'download',
                title: t('Download data'),
                testID: 'ActivityTimeline.Download',
              },
            ]}
            onSelect={download}
            testID="ActivityTimeline.More"
            style={styles.moreMenu}
          />
        )
      }
    >
      <View style={styles.content}>
        <SectionList
          sections={sections}
          renderSectionHeader={renderHeader}
          renderItem={renderItem}
          ListFooterComponent={
            messages.isFetchingNextPage ? (
              <ActivityIndicator style={styles.loading} color={Color.premiumBlack} />
            ) : !messages.isFetching && messages.hasNextPage ? (
              <Button
                title={t('Load more')}
                size="S"
                light
                style={styles.more}
                onPress={() => {
                  messages.fetchNextPage()
                }}
                testID="ActivityTimeline.LoadMore"
              />
            ) : null
          }
          ListEmptyComponent={<Text style={styles.empty}>{t('No activities')}</Text>}
          {...otherProps}
        />
        <ActivityFilter filter={filter} onChangeFilter={updateFilter} />
        <PopUp visible={!!messageInfo} onClose={() => setMessageInfo(false)} style={styles.popup}>
          <InfoTable companyId={companyId} config={messageInfo || { fields: [] }} />
        </PopUp>
      </View>
      <TimeZoneConflict companyId={companyId} buildingId={buildingId} />
    </Panel>
  )
}

interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style } = props
  const { t } = useTranslation()
  return (
    <Panel style={[styles.container, style]} title={t('Activities')}>
      <View style={styles.content}>
        <View style={styles.skeletonList} />
        <ActivityFilter.Skeleton />
      </View>
    </Panel>
  )
}

ActivityTimeline.Skeleton = Skeleton

export default ActivityTimeline
